import { PropsWithChildren, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { isGcpCostMenuTarget, useGcpCostEnabled } from '../../hooks/useGcpCostEnabled';
import type { IconComponent } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import StorageIcon from '@material-ui/icons/Storage';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import AnnouncementIcon from '@material-ui/icons/Announcement';

// 4-Tier RBAC: platform-admin / dept-admin / developer / viewer
type UserRole = 'platform-admin' | 'dept-admin' | 'developer' | 'viewer';

const PLATFORM_ADMIN_GROUP = 'group:default/platform-admins';
const DEPT_ADMIN_PATTERN = /^group:default\/[a-z]+-hq$/;

const ROLE_RANK: Record<UserRole, number> = {
  'platform-admin': 3,
  'dept-admin': 2,
  'developer': 1,
  'viewer': 0,
};

type MenuItem = {
  kind?: 'link' | 'my-groups' | 'divider';
  text?: string;
  to?: string;
  icon?: string;
  singularTitle?: string;
  pluralTitle?: string;
  requireAdmin?: boolean;
  requireRole?: string;
};

const defaultMenuItems: MenuItem[] = [
  { kind: 'link', text: 'Catalog', to: '/catalog', icon: 'home' },
  { kind: 'my-groups', singularTitle: 'My Group', pluralTitle: 'My Groups', icon: 'group' },
  { kind: 'link', text: 'APIs', to: 'api-docs', icon: 'apis' },
  { kind: 'link', text: 'Docs', to: 'docs', icon: 'docs' },
  { kind: 'link', text: 'Create...', to: 'create', icon: 'create' },
  { kind: 'link', text: 'Announcements', to: 'announcements', icon: 'announcements' },
  { kind: 'link', text: 'GitLab', to: 'gitlab', icon: 'gitlab', requireRole: 'platform-admin' },
  { kind: 'link', text: 'Alert Rules', to: 'admin/alert-rules', icon: 'alerts', requireRole: 'platform-admin' },
  { kind: 'link', text: 'GCP Cost', to: 'admin/gcp-cost', icon: 'gcp-cost', requireRole: 'platform-admin' },
];

const iconFor = (name?: string) => {
  switch ((name ?? '').toLowerCase()) {
    case 'home':
      return HomeIcon;
    case 'apis':
      return ExtensionIcon;
    case 'docs':
      return LibraryBooks;
    case 'create':
      return CreateComponentIcon;
    case 'gitlab':
      return StorageIcon;
    case 'alerts':
      return NotificationsActiveIcon;
    case 'gcp-cost':
      return AttachMoneyIcon;
    case 'announcements':
      return AnnouncementIcon;
    case 'group':
      return GroupIcon;
    default:
      return undefined;
  }
};

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const gcpCostEnabled = useGcpCostEnabled();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [userRole, setUserRole] = useState<UserRole>('viewer');

  useEffect(() => {
    async function resolveRole() {
      const identity = await identityApi.getBackstageIdentity();
      const refs = new Set(
        (identity.ownershipEntityRefs ?? []).map(r => r.toLowerCase()),
      );

      // T1: platform-admins 그룹 직접 멤버
      if (refs.has(PLATFORM_ADMIN_GROUP)) {
        setUserRole('platform-admin');
        return;
      }

      // T2: {dept}-hq 패턴 매치 (dept-admin)
      if (Array.from(refs).some(r => DEPT_ADMIN_PATTERN.test(r))) {
        setUserRole('dept-admin');
        return;
      }

      // T1/T2 카탈로그 폴백: platform-admins 또는 {dept}-hq의 자식 그룹인지 확인
      try {
        // platform-admins 자식 확인
        const paGroup = await catalogApi.getEntityByRef(PLATFORM_ADMIN_GROUP);
        if (paGroup) {
          const paChildren = (paGroup.spec?.children as string[]) ?? [];
          const paChildRefs = paChildren.map(c => `group:default/${c.toLowerCase()}`);
          if (paChildRefs.some(g => refs.has(g))) {
            setUserRole('platform-admin');
            return;
          }
        }
      } catch {
        // 카탈로그 조회 실패 시 무시
      }

      // T3: 그룹 소속이 있으면 developer
      if (Array.from(refs).some(r => r.startsWith('group:'))) {
        setUserRole('developer');
        return;
      }

      // T4: 그룹 미소속 → viewer
      setUserRole('viewer');
    }
    resolveRole();
  }, [identityApi, catalogApi]);

  const allMenuItems =
    configApi
      .getOptionalConfigArray('app.sidebar.menuItems')
      ?.map(cfg => ({
        kind: (cfg.getOptionalString('kind') as MenuItem['kind']) ?? 'link',
        text: cfg.getOptionalString('text'),
        to: cfg.getOptionalString('to'),
        icon: cfg.getOptionalString('icon'),
        singularTitle: cfg.getOptionalString('singularTitle'),
        pluralTitle: cfg.getOptionalString('pluralTitle'),
        requireAdmin: cfg.getOptionalBoolean('requireAdmin') ?? false,
        requireRole: cfg.getOptionalString('requireRole'),
      })) ?? defaultMenuItems;

  // 메뉴 필터링: requireRole 또는 requireAdmin 기반 (+ ★ D27: GCP Cost 플래그 off 면 `admin/gcp-cost` 링크 제거)
  const menuItems = allMenuItems.filter(item => {
    if (!gcpCostEnabled && isGcpCostMenuTarget(item.to)) {
      return false;
    }
    // requireRole 우선 (새 방식)
    if (item.requireRole) {
      const required = item.requireRole as UserRole;
      const requiredRank = ROLE_RANK[required] ?? 0;
      return ROLE_RANK[userRole] >= requiredRank;
    }
    // requireAdmin 하위 호환 (platform-admin만 허용)
    if (item.requireAdmin) {
      return userRole === 'platform-admin';
    }
    return true;
  });

  const renderMenuItem = (item: MenuItem, index: number) => {
    if (item.kind === 'divider') {
      return <SidebarDivider key={`divider-${index}`} />;
    }

    if (item.kind === 'my-groups') {
      return (
        <MyGroupsSidebarItem
          key={`my-groups-${index}`}
          singularTitle={item.singularTitle ?? 'My Group'}
          pluralTitle={item.pluralTitle ?? 'My Groups'}
          icon={iconFor(item.icon) ?? GroupIcon}
        />
      );
    }

    if (!item.text || !item.to) {
      return null;
    }

    const icon = iconFor(item.icon);
    return (
      <SidebarItem
        key={`${item.to}-${index}`}
        icon={icon as IconComponent}
        to={item.to}
        text={item.text}
      />
    );
  };

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {menuItems.map(renderMenuItem)}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <NotificationsSidebarItem />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
